import React from 'react'
import { Drawer, Empty, Spin, Timeline } from 'antd'
import isNil from 'lodash/isNil'
import { useUserHistoryQuery } from '../../api/user-invitation-api-client'
import { type UserListRow } from '../../api/dto/user-invitation.dto'
import {
  formatAuditCompanySyncDetail,
  formatAuditEvent,
  formatAuditRoleChangeDetail,
  formatDateTime,
  getAuditEventColor,
} from './userDisplay'
import {
  HistoryEmailHeader,
  HistoryEventLabel,
  HistoryMeta,
  HistoryPlaceholder,
} from './UserManagement.styles'

/** Lets the placeholder take the height left under the email header. */
const BODY_STYLE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
}

interface UserHistoryDrawerProps {
  user: UserListRow | null
  onClose: () => void
}

export const UserHistoryDrawer = ({
  user,
  onClose,
}: UserHistoryDrawerProps) => {
  const open = !isNil(user)
  const { data, isLoading, isError } = useUserHistoryQuery(user?.userId ?? null)
  const entries = data?.data ?? []

  return (
    <Drawer
      title="Invitation history"
      placement="right"
      width={420}
      open={open}
      onClose={onClose}
      destroyOnClose
      styles={{ body: BODY_STYLE }}
    >
      {user ? <HistoryEmailHeader>{user.email}</HistoryEmailHeader> : null}

      {isLoading ? (
        <HistoryPlaceholder>
          <Spin />
        </HistoryPlaceholder>
      ) : isError ? (
        <HistoryPlaceholder>
          <Empty description="Failed to load history" />
        </HistoryPlaceholder>
      ) : entries.length === 0 ? (
        <HistoryPlaceholder>
          <Empty description="No invitation activity yet" />
        </HistoryPlaceholder>
      ) : (
        <Timeline
          items={entries.map((entry) => {
            const detail =
              formatAuditRoleChangeDetail(entry.metadata) ??
              formatAuditCompanySyncDetail(entry.metadata)
            return {
              color: getAuditEventColor(entry.eventType),
              children: (
                <div>
                  <HistoryEventLabel>
                    {formatAuditEvent(entry.eventType)}
                  </HistoryEventLabel>
                  {detail ? <HistoryMeta>{detail}</HistoryMeta> : null}
                  <HistoryMeta>{formatDateTime(entry.createdAt)}</HistoryMeta>
                  {entry.adminUserId ? (
                    <HistoryMeta>by {entry.adminUserId}</HistoryMeta>
                  ) : null}
                </div>
              ),
            }
          })}
        />
      )}
    </Drawer>
  )
}
